// Wires the generated API client to the stored device session.
import { createApiClient, type Schemas } from "@10twenty/api";
import { SESSION_STORAGE_KEY, SessionManager } from "./auth/session";
import { withResponseTimeout } from "./fetchTimeout";

/** Longest wait for a GET's response headers (geocoder lookups may queue). */
const RESPONSE_TIMEOUT_MS = 20_000;

const baseUrl = new URL("/api/v1", window.location.origin).href;
const plainFetch = withResponseTimeout((request: Request) => fetch(request), RESPONSE_TIMEOUT_MS);

export const session = new SessionManager({
  baseUrl,
  storage: window.localStorage,
  fetch: plainFetch,
  locks: typeof navigator.locks === "undefined" ? undefined : navigator.locks,
});

// Sign-in and sign-out in other tabs.
window.addEventListener("storage", (event) => {
  if (event.key === null || event.key === SESSION_STORAGE_KEY) session.notifyExternalChange();
});

/** Typed REST client; attaches and refreshes the session's access token. */
export const api = createApiClient({ baseUrl, fetch: session.authFetch });

/** Client without credentials, for sign-in (a failed login is never retried). */
export const publicApi = createApiClient({ baseUrl, fetch: plainFetch });

/** A failed API call, carrying the RFC 9457 problem body when there is one. */
export class ApiError extends Error {
  constructor(
    readonly status: number,
    readonly problem: Partial<Schemas["Problem"]> | undefined,
  ) {
    super(problem?.detail ?? problem?.title ?? `Request failed (HTTP ${status})`);
    this.name = "ApiError";
  }

  get code(): string | undefined {
    return this.problem?.code;
  }
}

/** Returns `data` from an openapi-fetch result, throwing `ApiError` on failure. */
export function unwrap<T>(result: { data?: T; error?: unknown; response: Response }): T {
  if (result.error !== undefined || result.data === undefined) {
    const problem = typeof result.error === "object" && result.error !== null ? result.error : undefined;
    throw new ApiError(result.response.status, problem);
  }
  return result.data;
}

/** A message for the user; `overrides` maps HTTP statuses to friendlier text. */
export function errorMessage(error: unknown, overrides: Partial<Record<number, string>> = {}): string {
  if (error instanceof ApiError) {
    const override = overrides[error.status];
    if (override) return override;
    if (error.status === 429) return "Too many attempts. Wait a minute and try again.";
    return error.message;
  }
  if (error instanceof TypeError) return "Can't reach the server. Check your connection.";
  return error instanceof Error ? error.message : "Something went wrong.";
}
